import { LEVEL_RANK, LEVELS, SEAT_ROLES, type Level, type SeatRole } from './constants';
import type { Verdict } from './constants';
import type { ScalarFieldValue } from './module-kit';
import { m } from '@/i18n';
import { errOf, isRecord, ok, type Result } from './types';
import { carriesTag, parseTagPath } from './item-tags';
import { isClosed, lifecycleReading, worstTag, type Standings, type TagDef } from './tags';

/**
 * The shared search-criteria grammar: one typed filter criterion, the predicate
 * that evaluates it against an item's facts, and the URL codec that makes a
 * criteria set shareable. Extracted from the map view (docs/MAP_VIEW.md §3,
 * "search-as-filter") so every filtering surface - the map, the quick-open
 * palette, /search, the Work list - speaks the exact same grammar and can never
 * drift. Pure data + pure functions, no I/O; the server-side search re-applies
 * `criteriaMatch` after its SQL gather (docs/SEARCH.md), so SQL is only ever a
 * pre-filter, never a second definition of a criterion.
 *
 * The flow-definition build (docs/FLOW_DEFINITION.md §5, "one grammar with
 * search") extends the same grammar below with the state-vector dimensions
 * (docs/MACHINE.md §1) that stage display predicates, gate conditions, and
 * rule conditions need: `StateCriterion` is the full union, `VectorSnapshot`
 * the facts it evaluates over, `matchesStateCriterion`/`evaluatePredicate`
 * the ONE evaluator all four consumers (display predicates, When gates, rule
 * conditions, static analysis) share.
 *
 * The operators (docs/SEARCH.md "Criteria operators", settled 2026-10-14): a
 * criterion may be negated (`not: true`, except the four booleans, whose
 * negation is the other value), a set-valued attribute holds a list of values
 * matched by OR, and a set-valued attribute other than `judgment` can be asked
 * whether it holds nothing (`empty: true`).
 */

/** A set-valued attribute asked for ANY of its values (OR); negated, none of
 *  them. `values` is never empty after `normalizeCriteria`. */
export type AnyOfCriterion<F extends string, V extends string> = {
  field: F;
  values: V[];
  empty?: never;
  not?: true;
};

/** A set-valued attribute asked whether it holds nothing; negated, whether it
 *  holds something. */
export type EmptyCriterion<F extends string> = {
  field: F;
  empty: true;
  values?: never;
  not?: true;
};

/**
 * One typed filter criterion (a chip in a filter bar). Each field matches an
 * existing item fact - encoding surfaces existing data, never new fields.
 * Deliberately a small closed set of field kinds, combined by one all/any
 * operator. Three shapes (docs/SEARCH.md "Criteria operators"):
 *
 *   - a boolean `{ field, value }`, never negated (the negation is the other
 *     value, and `normalizeCriteria` folds a stored `not` into it);
 *   - a single value `{ field, value, not? }` (`title`, `level`);
 *   - a set-valued attribute (`SET_FIELDS`): `{ field, values, not? }` or
 *     `{ field, empty: true, not? }` (no empty form for `judgment`, whose
 *     `none` is a value).
 */
export type Criterion =
  // The lifecycle atoms as boolean criteria (docs/CLOSED_AXIS.md, the standing
  // retirement 2026-07-22): the bundled `standing` tri-state word is RETIRED -
  // a filter speaks the attributes themselves. Each reads the raw atom
  // (`active` persists through a close).
  | { field: 'closed'; value: boolean }
  | { field: 'active'; value: boolean }
  // Worst health at least this severe; negated, `Health < X`, which includes
  // an item with no signal.
  | { field: 'level'; value: Level; not?: true }
  // Case-insensitive substring.
  | { field: 'title'; value: string; not?: true }
  // A contributor ref, or `SELF_VALUE`; empty = unclaimed (the retired
  // `unclaimed` sentinel).
  | AnyOfCriterion<'assignee', string>
  | EmptyCriterion<'assignee'>
  // A party ref, or `SELF_VALUE` (docs/SEARCH.md "Parties"): one of the values
  // holds either seat, the item's requesters or its contributors. Empty = no
  // seat is held at all.
  | AnyOfCriterion<'party', string>
  | EmptyCriterion<'party'>
  // The item's effective groups, by group id: the groups it is granted to and
  // those it inherits up its parent chain. Empty = granted to no group.
  | AnyOfCriterion<'group', string>
  | EmptyCriterion<'group'>
  // A direct child of this item id, by any ACTIVE parent edge (2026-09-15: the
  // Work row shows its parent path, so what you can see you can filter).
  // Empty = no VISIBLE parent (`parentIds`), what the row shows.
  | AnyOfCriterion<'parent', string>
  | EmptyCriterion<'parent'>
  // Under this item id at any depth: the id is one of the item's VISIBLE
  // ancestors (`ancestorIds`), never the item itself (Leon, 2026-10-19: the
  // attribute names the parent relation, so the root is not its own parent).
  // The bar shows it as the parent attribute's "contains"; negated, it hides a
  // whole area of work. No empty form: "no ancestor" is "no parent". A view's
  // root (docs/VIEWS.md §2b) is the other way to scope to a subtree, a
  // membership setting on a table, where the root becomes the view.
  | AnyOfCriterion<'ancestor', string>
  // A user tag (docs/FIELDS_AND_TAGS.md §6.1): each value is a FOLDED path, and
  // the item's tag or any descendant matches (a filter on `area` matches
  // `area/frontend`, the Obsidian rule). Not the health catalog, whose flow
  // criterion is `signal` (VectorCriterion below).
  | AnyOfCriterion<'tag', string>
  | EmptyCriterion<'tag'>
  // The delivery and the judgment over it (docs/MACHINE.md §1, encoded over the
  // current atoms and verdict). Born as flow-predicate dimensions and moved
  // into the search grammar on 2026-10-03 (docs/LENSES.md §6c): "delivered,
  // awaiting review" is a filter and a custom group as much as a stage gate.
  //   - `tendered` - a standing delivery claim (the `delivered` atom);
  //   - `judgment` - the counterparty's statement over it (`none` = no verdict);
  //   - `cancelled` - concluded without a delivery (the cancellation reading);
  //   - `done` - concluded with the delivery standing (the done reading, added
  //     2026-11-03 so the conclusion's three values, Open / Done / Cancelled,
  //     are each one filter).
  | { field: 'tendered'; value: boolean }
  | AnyOfCriterion<'judgment', Judgment>
  | { field: 'cancelled'; value: boolean }
  | { field: 'done'; value: boolean };

export type CriteriaOp = 'all' | 'any';

export const CRITERION_FIELDS = [
  'closed',
  'active',
  'level',
  'assignee',
  'party',
  'group',
  'parent',
  'ancestor',
  'title',
  'tag',
  'tendered',
  'judgment',
  'cancelled',
  'done',
] as const;

/** The set-valued attributes: a criterion on one holds a list of values. */
export const SET_FIELDS = [
  'assignee',
  'party',
  'group',
  'parent',
  'ancestor',
  'tag',
  'judgment',
] as const;
export type SetField = (typeof SET_FIELDS)[number];

/** The set-valued attributes that can be asked whether they hold nothing
 *  (`judgment` cannot: its `none` is a value). */
export const EMPTY_FIELDS = ['assignee', 'party', 'group', 'parent', 'tag'] as const;
export type EmptyField = (typeof EMPTY_FIELDS)[number];

/** The boolean attributes: never negated, their negation is the other value. */
export const BOOLEAN_FIELDS = ['closed', 'active', 'tendered', 'cancelled', 'done'] as const;
export type BooleanField = (typeof BOOLEAN_FIELDS)[number];

export function isSetField(field: string): field is SetField {
  return (SET_FIELDS as readonly string[]).includes(field);
}

export function isEmptyField(field: string): field is EmptyField {
  return (EMPTY_FIELDS as readonly string[]).includes(field);
}

export function isBooleanField(field: string): field is BooleanField {
  return (BOOLEAN_FIELDS as readonly string[]).includes(field);
}

/** Is this criterion negated? (A boolean criterion never is.) */
export function isNegated(c: { not?: true } | object): boolean {
  return 'not' in c && c.not === true;
}

/**
 * The ROOT a filter names (docs/VIEWS.md §12, "The root chip", 2026-11-03):
 * the first `ancestor` criterion that is not negated and names exactly one
 * item, which the Work filter bar shows as its own chip ("Under <item>")
 * beside the bar. `rest` is every other criterion, in order, for the bar. A
 * negated or many-valued `ancestor` stays in the bar as its parent chip.
 */
export function splitRoot(criteria: readonly Criterion[]): {
  root: string | null;
  rest: Criterion[];
} {
  const at = criteria.findIndex(
    (c) => c.field === 'ancestor' && !isNegated(c) && 'values' in c && c.values.length === 1,
  );
  if (at < 0) return { root: null, rest: [...criteria] };
  const c = criteria[at] as { values: string[] };
  return {
    root: c.values[0] ?? null,
    rest: criteria.filter((_, i) => i !== at),
  };
}

/** `criteria` scoped under `root`: the bar's criteria with the root's one
 *  `ancestor` criterion after them, or without one for a null root. */
export function withRoot(rest: readonly Criterion[], root: string | null): Criterion[] {
  return root === null ? [...rest] : [...rest, { field: 'ancestor', values: [root] }];
}

/**
 * The value that stands for the person who opens the criteria (docs/SEARCH.md
 * "Parties"), in a `party` or an `assignee` criterion: their own ref and every
 * group through whose seats they act (`selfRefsOf`, src/domain/seats.ts, the
 * set the write spine's `holdsSeat` counts as theirs). It is resolved where the
 * criteria are EVALUATED (`bindSelf`), never where they are stored or copied,
 * so a lens holding it shows each opener their own work (docs/LENSES.md law 2).
 *
 * It is not a ref (a ref is `<kind>:<id>`), so a criterion that still holds it
 * matches no seat: `matchesCriterion` never resolves it, and a path that
 * forgets to bind shows fewer items, never more. A flow predicate refuses it
 * (`parseFlowPredicate`, src/domain/flow-definition.ts): a flow evaluates an
 * item for no one in particular.
 */
export const SELF_VALUE = 'self';

/** The criteria whose values may hold `SELF_VALUE`: the two that name seat
 *  holders. Every other field keeps the word as written (a tag or a title
 *  spelling "self" is that tag or that text). */
export const SELF_FIELDS = ['party', 'assignee'] as const;
export type SelfField = (typeof SELF_FIELDS)[number];

function isSelfField(field: string): field is SelfField {
  return (SELF_FIELDS as readonly string[]).includes(field);
}

/** Does this criterion hold `SELF_VALUE` in a field where it means the opener? */
export function holdsSelf(c: Criterion | StateCriterion): boolean {
  return (
    isSelfField(c.field) &&
    'values' in c &&
    !!c.values &&
    (c.values as readonly string[]).includes(SELF_VALUE)
  );
}

/**
 * The criteria as one opener evaluates them: `SELF_VALUE` in each `party` and
 * `assignee` values list replaced by `selfRefs` (the opener's `selfRefsOf`),
 * each list deduplicated in order. Every other criterion, and every value that
 * is not `SELF_VALUE`, stays as written: a named person stays literal, since a
 * filter on another person must not depend on their memberships, which the
 * opener may not see. With no refs (a narrowed agent whose grant reaches no
 * group and leaves out its own items) `SELF_VALUE` stays, and matches no seat,
 * which is what an empty set of refs means. Pure; the input is not changed.
 */
export function bindSelf<C extends Criterion>(
  criteria: readonly C[],
  selfRefs: readonly string[],
): C[] {
  if (selfRefs.length === 0 || !criteria.some(holdsSelf)) return [...criteria];
  return criteria.map((c) => {
    if (!holdsSelf(c) || !('values' in c) || !c.values) return c;
    const values: string[] = [];
    for (const v of c.values as readonly string[]) {
      for (const bound of v === SELF_VALUE ? selfRefs : [v]) {
        if (!values.includes(bound)) values.push(bound);
      }
    }
    return { ...c, values };
  });
}

/**
 * The judgment vocabulary (MACHINE.md §1 #4: `none · accepted · disputed`) over
 * the CURRENT verdict encoding: verdict `open` reads as judgment `none`. A
 * grammar word, not a stored enum - the stored axis stays `Verdict`
 * (constants.ts); this lives with the grammar that speaks it.
 */
export const JUDGMENTS = ['none', 'accepted', 'disputed'] as const;
export type Judgment = (typeof JUDGMENTS)[number];

/** The judgment an item's stored verdict reads as. */
export function judgmentOf(verdict: Verdict): Judgment {
  return verdict === 'open' ? 'none' : verdict;
}

/** The item facts the filter predicate reads - a structural pick of the read
 *  DTOs (MapNode, WorkItemListing, search hits), named here so the domain layer
 *  never depends on any of them. */
export interface FilterableNode extends Standings {
  title: string;
  /** The held requester seats, ref-encoded (`work_items.requesters`). */
  requesters: string[];
  /** The held contributor seats, ref-encoded (`work_items.contributors`). */
  contributors: string[];
  tags: TagDef[];
  groupIds: string[];
  /** The ids at the far end of the item's active `parent` edges - the VISIBLE
   *  ones only, where the read knows the viewer (`domain/parent-path.ts`: an
   *  invisible parent is omitted, never counted), so a filter can never say
   *  what a row could not show. */
  parentIds: string[];
  /** Every VISIBLE ancestor's id: the upward closure over the visible parent
   *  edges (`ancestorSetsOf`, `domain/parent-path.ts`), the item itself
   *  excluded; a parent the viewer cannot see ends that line. The `ancestor`
   *  criterion's fact. */
  ancestorIds: string[];
  /** The user tags the item carries, as spelled (docs/FIELDS_AND_TAGS.md §6);
   *  the `tag` criterion compares them by fold. Distinct from `tags`, the
   *  health catalog's resolved signals. */
  itemTags: string[];
  /** The stored verdict: the `judgment` criterion's fact. */
  verdict: Verdict;
}

/** Does one criterion accept this node? The positive reading is decided per
 *  field below; a negated criterion is its inverse, here and nowhere else. */
export function matchesCriterion(node: FilterableNode, c: Criterion): boolean {
  const hit = matchesPositive(node, c);
  return isNegated(c) ? !hit : hit;
}

/** A set-valued criterion over the item's set: any of the values (by `has`),
 *  or the set being empty. */
function matchesSet<V>(
  c: { values: V[]; empty?: never } | { empty: true; values?: never },
  size: number,
  has: (v: V) => boolean,
): boolean {
  return c.empty ? size === 0 : c.values.some(has);
}

/** The criterion's reading with any `not` ignored. */
function matchesPositive(node: FilterableNode, c: Criterion): boolean {
  switch (c.field) {
    case 'closed':
      return node.closed === c.value;
    case 'active':
      return node.active === c.value;
    case 'level': {
      const worst = worstTag(node.tags);
      return worst !== null && LEVEL_RANK[worst.level] >= LEVEL_RANK[c.value];
    }
    case 'assignee':
      // A filter, not an authority test: "show items whose contributor set
      // contains one of these refs", compared literally. The opener's groups
      // enter only through `SELF_VALUE`, which `bindSelf` replaced before
      // this runs; an unbound one is no ref and matches no seat.
      return matchesSet(c, node.contributors.length, (ref) =>
        // eslint-disable-next-line no-restricted-syntax -- filter criterion over the item's seat set, not an actor's authority
        node.contributors.includes(ref),
      );
    case 'party':
      // The same literal reading over both seats: a seat a group holds
      // matches the group's ref, never its members (the opener's groups come
      // in through a bound `SELF_VALUE`, as for `assignee`).
      return matchesSet(
        c,
        node.requesters.length + node.contributors.length,
        // eslint-disable-next-line no-restricted-syntax -- filter criterion over the item's seat sets, not an actor's authority
        (ref) => node.requesters.includes(ref) || node.contributors.includes(ref),
      );
    case 'group':
      return matchesSet(c, node.groupIds.length, (id) => node.groupIds.includes(id));
    case 'parent':
      return matchesSet(c, node.parentIds.length, (id) => node.parentIds.includes(id));
    case 'ancestor':
      return c.values.some((id) => node.ancestorIds.includes(id));
    case 'title':
      return node.title.toLowerCase().includes(c.value.toLowerCase());
    case 'tag':
      // A value matches the tag and every descendant, so `not #area` never
      // shows an `area/frontend` item.
      return matchesSet(c, node.itemTags.length, (fold) => carriesTag(node.itemTags, fold));
    case 'tendered':
      // The delivery atom directly (docs/CLOSED_AXIS.md): a standing claim.
      return node.delivered === c.value;
    case 'judgment':
      return c.values.includes(judgmentOf(node.verdict));
    case 'cancelled':
      // The cancellation READING, through the one function that holds it
      // (docs/CLOSED_AXIS.md; `lifecycleReading`, 2026-09-20): a filter and a
      // chip must never disagree about what cancelled means.
      return (lifecycleReading(node) === 'cancelled') === c.value;
    case 'done':
      // The done reading, through the same function (2026-11-03).
      return (lifecycleReading(node) === 'done') === c.value;
  }
}

/** Does the whole criteria set accept this node? Empty criteria accept everything;
 *  `all` requires every criterion, `any` requires at least one. */
export function criteriaMatch(
  node: FilterableNode,
  criteria: Criterion[],
  op: CriteriaOp,
): boolean {
  if (criteria.length === 0) return true;
  const hit = (c: Criterion) => matchesCriterion(node, c);
  return op === 'any' ? criteria.some(hit) : criteria.every(hit);
}

/** One value of a set-valued attribute, validated: the tag's fold, a known
 *  judgment word, or a non-empty ref/id (existence is the graph's concern). */
function setValueOf(field: SetField, raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  switch (field) {
    case 'tag': {
      // Stored and wire values alike run through the one path decider, and
      // the criterion keeps the FOLD (a filter is case-insensitive, §6.1).
      const parsed = parseTagPath(raw);
      return parsed.ok ? parsed.value.fold : null;
    }
    case 'judgment':
      return (JUDGMENTS as readonly string[]).includes(raw) ? raw : null;
    case 'assignee':
    case 'party':
    case 'group':
    case 'parent':
    case 'ancestor':
      return raw.length > 0 ? raw : null;
  }
}

/** One stored or wire criterion, validated into the grammar, or null. */
function normalizeOne(raw: unknown): Criterion | null {
  if (!raw || typeof raw !== 'object') return null;
  const c = raw as Record<string, unknown>;
  const not = c.not === true;
  // Each criterion is built from a literal in a fixed key order (field, then
  // values / empty / value, then not), so the serialization is canonical.
  const neg = not ? ({ not: true } as const) : {};
  if (typeof c.field !== 'string') return null;
  if (isBooleanField(c.field)) {
    // Stored payloads carry the boolean; the URL codec speaks yes/no. One
    // normalizer accepts both so the two paths can never drift. A stored `not`
    // folds into the other value: a boolean is never negated.
    const v = atomValue(c.value);
    return v === null ? null : { field: c.field, value: not ? !v : v };
  }
  if (isSetField(c.field)) {
    const field = c.field;
    if (c.empty === true) {
      return isEmptyField(field) ? { field, empty: true, ...neg } : null;
    }
    if (!Array.isArray(c.values)) return null;
    const values: string[] = [];
    for (const v of c.values) {
      const value = setValueOf(field, v);
      if (value !== null && !values.includes(value)) values.push(value);
    }
    if (values.length === 0) return null;
    return field === 'judgment'
      ? { field, values: values as Judgment[], ...neg }
      : { field, values, ...neg };
  }
  if (typeof c.value !== 'string') return null;
  if (c.field === 'level') {
    return (LEVELS as readonly string[]).includes(c.value)
      ? { field: 'level', value: c.value as Level, ...neg }
      : null;
  }
  if (c.field === 'title') {
    return c.value.length > 0 ? { field: 'title', value: c.value, ...neg } : null;
  }
  return null;
}

/** Validate stored or wire criteria: keep only well-formed entries with known
 *  values, so a payload from an older row, a hand-edited URL, or the client can
 *  never become a bad predicate. Refs/ids are only type-checked (existence is
 *  the graph's concern). A values list is deduplicated in its given order. */
export function normalizeCriteria(input: unknown): Criterion[] {
  if (!Array.isArray(input)) return [];
  const out: Criterion[] = [];
  for (const raw of input) {
    const c = normalizeOne(raw);
    if (c) out.push(c);
  }
  return out;
}

/** A boolean criterion's value from either encoding: the stored boolean or
 *  the URL's yes/no word. Anything else is malformed (null). */
function atomValue(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return null;
}

/** The prefix a negated criterion's URL key takes (`-tag=legacy`). */
const NEGATED_PARAM = '-';

/**
 * The URL's explicit empty filter, `?filter=none`. An address with no criteria
 * shows the view's SAVED criteria (the overlay rule, docs/VIEWS.md §12), so on
 * a view whose saved config carries a filter, removing the last chip could
 * only fall back to it. A filter bar writes this param instead when its last
 * chip goes on such a view: the address then carries a filter of its own, and
 * that filter is empty (`criteriaFromParams`'s `own`).
 */
export const NO_FILTER_PARAM = 'filter';
export const NO_FILTER_VALUE = 'none';

/** Every URL key the criteria vocabulary owns: each field, each field
 *  negated, and the explicit empty filter. A surface that rewrites the
 *  criteria in place deletes these. */
export const CRITERION_PARAM_KEYS: readonly string[] = [
  ...CRITERION_FIELDS.flatMap((f) => [f, `${NEGATED_PARAM}${f}`]),
  NO_FILTER_PARAM,
];

/** One criterion's URL key: the field, with a leading `-` when negated. */
export function criterionParamKey(c: Criterion): string {
  return isNegated(c) ? `${NEGATED_PARAM}${c.field}` : c.field;
}

/** One criterion's URL value: yes/no for a boolean, the values comma-joined
 *  for a set, `''` for the empty form, the text or level word otherwise. */
export function criterionParamValue(c: Criterion): string {
  if ('values' in c && c.values) return c.values.join(',');
  if ('empty' in c && c.empty) return '';
  if ('value' in c) return typeof c.value === 'boolean' ? (c.value ? 'yes' : 'no') : c.value;
  return '';
}

/**
 * The URL grammar for a criteria set: `q` carries free text, each criterion is a
 * repeated `<field>=<value>` param (`closed=yes|no`, `active=yes|no`, `level=`,
 * `assignee=` and `party=` (a ref, or `self`, `SELF_VALUE`), `group=<group id>`,
 * `parent=<item id>`, `ancestor=<item id>`, `title=`, `tag=<path>`,
 * `tendered=yes|no`, `judgment=none|accepted|disputed`, `cancelled=yes|no`,
 * `done=yes|no`), and
 * `op=any` appears only when set (all is the default). A set-valued field's
 * values are comma-joined (no tag, ref, id or judgment word holds a comma), its
 * empty form is the empty value (`parent=`), and a negated criterion's key takes
 * a leading `-` (`-tag=legacy`). A repeated key is a second criterion. The
 * field names do not collide with the Work list's `sort`/`grouping`/`dir` or
 * the trail's `from`/`trail`, so the vocabularies can share one URL.
 */
export function criteriaToParams(
  criteria: Criterion[],
  op: CriteriaOp,
  q?: string,
): URLSearchParams {
  const sp = new URLSearchParams();
  const text = q?.trim();
  if (text) sp.set('q', text);
  for (const c of criteria) sp.append(criterionParamKey(c), criterionParamValue(c));
  if (op === 'any' && criteria.length > 1) sp.set('op', 'any');
  return sp;
}

/** A criterion as one canonical string, its values in sorted order: two
 *  criteria that accept the same items have the same key (`sameCriteria`). */
export function criterionKey(c: Criterion): string {
  const value = 'values' in c && c.values ? [...c.values].sort().join(',') : criterionParamValue(c);
  return `${criterionParamKey(c)}=${value}`;
}

/** One URL param back into a raw criterion (validated by `normalizeCriteria`). */
function rawFromParam(field: string, value: string, not: boolean): Record<string, unknown> {
  const neg = not ? { not: true } : {};
  if (!isSetField(field)) return { field, value, ...neg };
  if (value === '') return { field, empty: true, ...neg };
  return { field, values: value.split(',').filter((v) => v.length > 0), ...neg };
}

/** Parse a Next `searchParams` object back into `{criteria, op, q, own}`.
 *  Values run through `normalizeCriteria`, so a hand-edited URL can never
 *  become a bad predicate; unknown params are ignored (they belong to other
 *  vocabularies). The criteria come back in field order, each field's positive
 *  criteria before its negated ones. `own` says whether the address carries a
 *  filter of its own, which then wins whole over a view's saved one: a
 *  criterion, free text, or the explicit empty filter (`NO_FILTER_PARAM`). */
export function criteriaFromParams(sp: Record<string, string | string[] | undefined>): {
  criteria: Criterion[];
  op: CriteriaOp;
  q: string;
  own: boolean;
} {
  const raw: Record<string, unknown>[] = [];
  for (const field of CRITERION_FIELDS) {
    for (const not of [false, true]) {
      const v = sp[not ? `${NEGATED_PARAM}${field}` : field];
      for (const value of typeof v === 'string' ? [v] : (v ?? [])) {
        raw.push(rawFromParam(field, value, not));
      }
    }
  }
  const qv = sp.q;
  const criteria = normalizeCriteria(raw);
  const q = (typeof qv === 'string' ? qv : (qv?.[0] ?? '')).trim();
  const none = sp[NO_FILTER_PARAM];
  const cleared = (typeof none === 'string' ? [none] : (none ?? [])).includes(NO_FILTER_VALUE);
  return {
    criteria,
    op: sp.op === 'any' ? 'any' : 'all',
    q,
    own: criteria.length > 0 || q.length > 0 || cleared,
  };
}

/**
 * Show closed: a cross-cutting view display flag, NOT a criterion. A
 * CONCLUDED item (`isClosed`: the closed atom, docs/CLOSED_AXIS.md - never
 * delivered alone) is dropped from every view surface unless the view's
 * `showClosed` flag is set. Applied ALONGSIDE `criteriaMatch`, never inside
 * it: criteria are an all/any set where empty means "everything", so a filter
 * that must hold regardless of the operator can never be one of them.
 * (docs/VIEWS.md "Closed items".)
 */
export function passesShowClosed(item: Pick<Standings, 'closed'>, showClosed: boolean): boolean {
  return showClosed || !isClosed(item);
}

/**
 * The EFFECTIVE show-closed for a filtering surface: the flag defers to
 * an explicit criterion on how the item ended (`closed`, `done`, `cancelled`,
 * the conclusion's three words since 2026-11-03). When the active criteria
 * already speak about it, the user (or the saved view) is choosing directly,
 * so a `done: yes` view must show done work even with `showClosed` off - the
 * default-hide only governs views that DON'T mention the conclusion.
 * Pass the criteria actually being applied.
 */
const ENDING_FIELDS: ReadonlySet<string> = new Set(['closed', 'done', 'cancelled']);

export function effectiveShowClosed(
  criteria: readonly { field: string }[],
  showClosed: boolean,
): boolean {
  return showClosed || criteria.some((c) => ENDING_FIELDS.has(c.field));
}

/**
 * The ephemeral show-closed override in a URL. `showClosed=1` forces closed
 * items visible, `showClosed=0` forces them hidden, and an absent param defers
 * to the saved view flag (returns `null`) - the same "URL overlays the saved
 * config" shape the criteria codec uses, as a tri-state so a peek can override
 * a saved `showClosed: true` in either direction. (The bare `closed` param is
 * the CRITERION - filter to closed/open work - since the standing retirement;
 * this override peeks without filtering.)
 */
export const CLOSED_PARAM = 'showClosed';

export function closedFromParams(
  sp: Record<string, string | string[] | undefined>,
): boolean | null {
  const v = sp[CLOSED_PARAM];
  const s = typeof v === 'string' ? v : Array.isArray(v) ? v[0] : undefined;
  if (s === '1') return true;
  if (s === '0') return false;
  return null;
}

/**
 * The listing budget (docs/LENSES.md "The listing budget"): how many rows a
 * lens page reads in its statement when the criteria and the sort compile to
 * SQL, per group when a grouping is in force. The list rail reads the next
 * budget of a list, or of a group, that holds fewer rows than match, where the
 * reader reaches its end.
 * `?n=` overrides it for one page, clamped to {@link LISTING_BUDGET_MAX}; a
 * value that is not a positive whole number reads as the default. Ephemeral
 * like `?showClosed=`: never saved on the lens.
 */
export const LISTING_BUDGET = 200;
export const LISTING_BUDGET_MAX = 5000;
export const BUDGET_PARAM = 'n';

export function budgetFromParams(sp: Record<string, string | string[] | undefined>): number {
  const v = sp[BUDGET_PARAM];
  const s = typeof v === 'string' ? v : Array.isArray(v) ? v[0] : undefined;
  if (s === undefined || !/^\d+$/.test(s)) return LISTING_BUDGET;
  const n = Number(s);
  return n < 1 ? LISTING_BUDGET : Math.min(n, LISTING_BUDGET_MAX);
}

// --- The state-vector extension (docs/FLOW_DEFINITION.md §5, MACHINE.md §1) ---

/**
 * The vector dimensions (MACHINE.md §1) the base search fields lack. Encoding
 * choices, per the current atoms/verdict encoding (MACHINE.md §6: `done` =
 * the three-atom readings, docs/CLOSED_AXIS.md). `tendered`, `judgment` and
 * `cancelled` started here and moved into the base `Criterion` on 2026-10-03
 * (docs/LENSES.md §6c); the one evaluator below still covers them by delegation.
 *   - `seat` - a HELD seat of the item, set-valued like `assignee`, both
 *     roles: `values` (any of these refs hold it), or `empty` (the seat is
 *     open; negated, someone holds it). The `anyone` / `none` holder sentinels
 *     retired into this form (docs/SEARCH.md "Criteria operators");
 *   - `signal` - a worn health catalog key (the condition set, by key not by grade);
 *   - `fieldValue` - a module/flow field equals a scalar (`''` ≡ null ≡ unset,
 *     the `normalizeEmptyScalar` normalization).
 */
export type VectorCriterion =
  | (AnyOfCriterion<'seat', string> & { role: SeatRole })
  | (EmptyCriterion<'seat'> & { role: SeatRole })
  | { field: 'signal'; value: string }
  | { field: 'fieldValue'; moduleKey: string; fieldKey: string; value: ScalarFieldValue };

export const VECTOR_CRITERION_FIELDS = ['seat', 'signal', 'fieldValue'] as const;

/** The full grammar: one union, so a stage predicate may mix vector dimensions
 *  with the search fields (a user who learns to filter has learned to gate). */
export type StateCriterion = Criterion | VectorCriterion;

/**
 * The state-vector snapshot the evaluator reads (MACHINE.md §1, in the current
 * encoding): the atoms/verdict, the held seats, the worn tags, plus the base
 * `FilterableNode` facts so the ONE evaluator covers the whole grammar.
 * `fieldValues` mirrors `module_data`: moduleKey -> fieldKey -> scalar (flow
 * fields under `flow:<id>`, registry modules under their own key).
 */
export interface VectorSnapshot extends FilterableNode {
  fieldValues: Record<string, Record<string, ScalarFieldValue> | undefined>;
}

/** The one scalar normalization (`'' ≡ null ≡ unset`): every comparison of
 *  stored field scalars - here, the automation guards, the static analysis -
 *  runs both sides through this first. */
export const normalizeEmptyScalar = (v: ScalarFieldValue): ScalarFieldValue =>
  v === '' ? null : v;
const normEmpty = normalizeEmptyScalar;

/** Does one criterion of the full grammar accept this snapshot? The base
 *  search fields delegate to `matchesCriterion` - one definition, never two. */
export function matchesStateCriterion(snap: VectorSnapshot, c: StateCriterion): boolean {
  switch (c.field) {
    case 'seat': {
      const held = c.role === 'requester' ? snap.requesters : snap.contributors;
      const hit = matchesSet(c, held.length, (ref) => held.includes(ref));
      return isNegated(c) ? !hit : hit;
    }
    case 'signal':
      return snap.tags.some((t) => t.key === c.value);
    case 'fieldValue':
      return normEmpty(snap.fieldValues[c.moduleKey]?.[c.fieldKey] ?? null) === normEmpty(c.value);
    default:
      return matchesCriterion(snap, c);
  }
}

/** `criteriaMatch` over the full grammar: empty criteria accept everything,
 *  `all` requires every criterion, `any` at least one. */
export function evaluateCriteria(
  snap: VectorSnapshot,
  criteria: StateCriterion[],
  op: CriteriaOp,
): boolean {
  if (criteria.length === 0) return true;
  const hit = (c: StateCriterion) => matchesStateCriterion(snap, c);
  return op === 'any' ? criteria.some(hit) : criteria.every(hit);
}

/**
 * A stored predicate over the vector: the payload shape a flow definition's
 * display predicates (`derivePredicate`), gates (`whenIn`/`whenOut`), and rule
 * conditions all carry, and the future static analysis reads. Empty criteria
 * accept everything (`criteriaMatch` semantics).
 */
export interface CriteriaPredicate {
  op: CriteriaOp;
  criteria: StateCriterion[];
}

/** THE evaluator (docs/FLOW_DEFINITION.md S1): every predicate consumer -
 *  display predicates, When gates, rule conditions - calls this one function. */
export function evaluatePredicate(snap: VectorSnapshot, pred: CriteriaPredicate): boolean {
  return evaluateCriteria(snap, pred.criteria, pred.op);
}

/** Strict parse of one criterion of the full grammar (the authoring path: a
 *  malformed criterion is an error, never a silent drop - contrast
 *  `normalizeCriteria`, the lenient storage-read path for the base fields). */
export function parseStateCriterion(value: unknown): Result<StateCriterion> {
  if (!isRecord(value)) return errOf('invalid_input', m('accessErrors.criteria.notObject'));
  // A boolean's `not` folds into the other value, as `normalizeCriteria` does.
  const flip = value.not === true;
  const neg = flip ? ({ not: true } as const) : {};
  switch (value.field) {
    case 'tendered':
      if (typeof value.value !== 'boolean') {
        return errOf('invalid_input', m('accessErrors.criteria.tenderedBoolean'));
      }
      return ok({ field: 'tendered', value: flip ? !value.value : value.value });
    case 'cancelled':
      if (typeof value.value !== 'boolean') {
        return errOf('invalid_input', m('accessErrors.criteria.cancelledBoolean'));
      }
      return ok({ field: 'cancelled', value: flip ? !value.value : value.value });
    case 'done':
      if (typeof value.value !== 'boolean') {
        return errOf('invalid_input', m('accessErrors.criteria.doneBoolean'));
      }
      return ok({ field: 'done', value: flip ? !value.value : value.value });
    case 'judgment': {
      const values = Array.isArray(value.values) ? value.values : [];
      if (
        values.length === 0 ||
        !values.every((v) => (JUDGMENTS as readonly unknown[]).includes(v))
      ) {
        return errOf(
          'invalid_input',
          m('accessErrors.criteria.judgmentOneOf', { values: JUDGMENTS.join(' | ') }),
        );
      }
      return ok({ field: 'judgment', values: [...new Set(values as Judgment[])], ...neg });
    }
    case 'seat': {
      if (!(SEAT_ROLES as readonly unknown[]).includes(value.role)) {
        return errOf(
          'invalid_input',
          m('accessErrors.criteria.seatRole', { roles: SEAT_ROLES.join(' | ') }),
        );
      }
      const role = value.role as SeatRole;
      if (value.empty === true) return ok({ field: 'seat', role, empty: true, ...neg });
      const raw = Array.isArray(value.values) ? value.values : [];
      const holders = raw.map((v) => (typeof v === 'string' ? v.trim() : ''));
      if (holders.length === 0 || holders.some((h) => h.length === 0)) {
        return errOf('invalid_input', m('accessErrors.criteria.seatHolder'));
      }
      return ok({ field: 'seat', role, values: [...new Set(holders)], ...neg });
    }
    case 'signal': {
      const key = typeof value.value === 'string' ? value.value.trim() : '';
      if (key.length === 0) return errOf('invalid_input', m('accessErrors.criteria.signalKey'));
      return ok({ field: 'signal', value: key });
    }
    case 'fieldValue': {
      const moduleKey = typeof value.moduleKey === 'string' ? value.moduleKey.trim() : '';
      const fieldKey = typeof value.fieldKey === 'string' ? value.fieldKey.trim() : '';
      if (moduleKey.length === 0 || fieldKey.length === 0) {
        return errOf('invalid_input', m('accessErrors.criteria.fieldKeys'));
      }
      const v = value.value === undefined ? null : value.value;
      if (v !== null && typeof v !== 'string' && typeof v !== 'number') {
        return errOf('invalid_input', m('accessErrors.criteria.fieldScalar'));
      }
      return ok({ field: 'fieldValue', moduleKey, fieldKey, value: v });
    }
    case 'tag':
      // The user tag criterion is a SEARCH field, not yet a flow one: the flow
      // snapshots (read/vector-snapshot.ts, the automation runtime) do not
      // carry the user tags, so a stage predicate on one could never hold
      // (the board item "The user tag system v1: what was built and what was
      // deferred"). A health key is the `signal` criterion.
      return errOf('invalid_input', m('accessErrors.criteria.userTag'));
    case 'ancestor':
      // A filter field, not yet a flow one (2026-10-19): the snapshots carry no
      // ancestors, and a predicate naming items at any depth is a flow
      // definition's reach into the tree, its own question (the board item "A
      // flow predicate cannot name an ancestor").
      // A direct parent is the `parent` criterion.
      return errOf('invalid_input', m('accessErrors.criteria.ancestor'));
    default: {
      // Base search fields ride the same strict gate through the one existing
      // validator: exactly one well-formed criterion in = one out, and a values
      // list keeps every distinct value it was given (the lenient path drops a
      // malformed one; the authoring path refuses it).
      const base = normalizeCriteria([value]);
      const c = base[0];
      const given = Array.isArray(value.values) ? new Set(value.values).size : null;
      const kept = c && 'values' in c && c.values ? c.values.length : null;
      return base.length === 1 && c && given === kept
        ? ok(c)
        : errOf('invalid_input', m('accessErrors.criteria.malformed'));
    }
  }
}

/** Strict parse of a stored predicate: `{ op?, criteria }`, op defaulting to
 *  `all`. Any malformed criterion fails the whole predicate (authoring path). */
export function parseCriteriaPredicate(value: unknown): Result<CriteriaPredicate> {
  if (!isRecord(value))
    return errOf('invalid_input', m('accessErrors.criteria.predicateNotObject'));
  const op = value.op === undefined ? 'all' : value.op;
  if (op !== 'all' && op !== 'any') {
    return errOf('invalid_input', m('accessErrors.criteria.predicateOp'));
  }
  if (!Array.isArray(value.criteria)) {
    return errOf('invalid_input', m('accessErrors.criteria.predicateList'));
  }
  const criteria: StateCriterion[] = [];
  for (const raw of value.criteria) {
    const c = parseStateCriterion(raw);
    if (!c.ok) return c;
    criteria.push(c.value);
  }
  return ok({ op, criteria });
}
